import { Loader2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import type { ActivityFeedItem } from '@/types/api/dashboard'
import { formatDateTime } from '@/utils/date'
import { displayActivityValue } from '@/utils/file-utils'

interface ActivityFeedWidgetProps {
  title: string
  items?: ActivityFeedItem[]
  hasMore?: boolean
  widgetId: string
  dataSource: string
  dataSourceConfig: Record<string, unknown>
  onLoadMore?: (widgetId: string, dataSource: string, config: Record<string, unknown>) => Promise<void>
}

const ACTION_COLORS: Record<string, { dot: string; bg: string; border: string }> = {
  create: { dot: 'bg-emerald-500', bg: 'bg-emerald-50/50', border: 'border-emerald-200/50' },
  update: { dot: 'bg-blue-500', bg: 'bg-blue-50/50', border: 'border-blue-200/50' },
  delete: { dot: 'bg-red-500', bg: 'bg-red-50/50', border: 'border-red-200/50' },
  view: { dot: 'bg-purple-500', bg: 'bg-purple-50/50', border: 'border-purple-200/50' },
  status_change: { dot: 'bg-amber-500', bg: 'bg-amber-50/50', border: 'border-amber-200/50' },
}

function getActionColor(actionType: string) {
  return ACTION_COLORS[actionType.toLowerCase()] ?? { dot: 'bg-neutral-400', bg: 'bg-neutral-50/50', border: 'border-neutral-200/50' }
}

export function ActivityFeedWidget({
  title,
  items = [],
  hasMore,
  widgetId,
  dataSource,
  dataSourceConfig,
  onLoadMore,
}: ActivityFeedWidgetProps) {
  const [loading, setLoading] = useState(false)

  const handleLoadMore = async () => {
    if (!onLoadMore || loading) return
    setLoading(true)
    try {
      await onLoadMore(widgetId, dataSource, dataSourceConfig)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col p-4">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-5 w-5 items-center justify-center rounded bg-gradient-to-br from-rose-500 to-pink-500 text-[10px] text-white font-bold">↻</div>
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{title}</p>
      </div>
      {items.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-neutral-400">No recent activity</p>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gradient-to-b from-rose-300 via-pink-300 to-transparent" />
          {items.map((item) => {
            const color = getActionColor(item.actionType)
            return (
              <div key={item.id} className={`relative ml-7 mb-4 rounded-lg ${color.bg} border ${color.border} p-3 transition-all duration-150 hover:shadow-sm`}>
                <div className={`absolute left-[-20px] top-4 h-[10px] w-[10px] rounded-full ring-[3px] ring-white ${color.dot}`} />
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-neutral-900">{item.actorName}</p>
                    <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${color.bg} ${color.dot.replace('bg-', 'text-')}`}>
                      {item.actionType}
                    </span>
                  </div>
                  <p className="shrink-0 text-[10px] text-neutral-400">{formatDateTime(item.createdAt)}</p>
                </div>
                {item.note ? <p className="mt-1.5 text-sm text-neutral-600">{item.note}</p> : null}
                {item.redacted ? (
                  <p className="mt-1.5 text-[11px] italic text-neutral-400">Updated a restricted field</p>
                ) : null}
                {item.fieldLabel ? (
                  <p className="mt-1.5 text-[11px] text-neutral-500">
                    <span className="font-medium text-neutral-700">{item.fieldLabel}</span>:{' '}
                    <span className="line-through text-neutral-400">
                      {displayActivityValue(item.oldValue, item.fieldType)}
                    </span>
                    {' → '}
                    <span className="font-medium text-emerald-600">
                      {displayActivityValue(item.newValue, item.fieldType)}
                    </span>
                  </p>
                ) : null}
              </div>
            )
          })}
          {hasMore && (
            <div className="flex justify-center pb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadMore}
                disabled={loading}
                className="text-xs"
              >
                {loading && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
